import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Suppress cross-origin image/resource load errors ("Script error.") from
// triggering the CRA dev overlay. These are benign browser security artifacts
// that occur when presigned S3 URLs expire — not real JavaScript exceptions.
//
// Also auto-recovers from stale-deploy ChunkLoadError: after a new deploy
// the old index.html cached in the browser still references PREVIOUS webpack
// chunk hashes (`Dashboard.7b2c.js`); those files no longer exist on the
// server, causing dynamic imports to throw `ChunkLoadError`. We catch that
// and trigger a single hard-reload so the browser fetches the fresh
// index.html + new chunks. The sessionStorage flag prevents an infinite
// reload loop in case the reload itself somehow surfaces the same error.
const STALE_BUNDLE_RELOAD_KEY = '__cop31_stale_bundle_reload_at';
const STALE_BUNDLE_LOOP_WINDOW_MS = 30_000; // single recovery per 30s

function _isChunkLoadError(message, error) {
  if (!message && !error) return false;
  const msg = String(message || '').toLowerCase();
  const reason = String(error?.message || error?.name || '').toLowerCase();
  const hay = `${msg} ${reason}`;
  return (
    hay.includes('chunkloaderror') ||
    hay.includes('loading chunk') ||
    hay.includes('loading css chunk') ||
    hay.includes('failed to fetch dynamically imported module') ||
    hay.includes("dynamically imported module") ||
    (hay.includes('importing a module script failed'))
  );
}

function _attemptStaleBundleRecovery(reasonLabel) {
  try {
    const last = Number(sessionStorage.getItem(STALE_BUNDLE_RELOAD_KEY) || 0);
    if (Date.now() - last < STALE_BUNDLE_LOOP_WINDOW_MS) {
      // We just reloaded; abort to avoid a loop. Surface the error normally.
      // eslint-disable-next-line no-console
      console.warn('[stale-bundle] reload skipped (recent attempt)', reasonLabel);
      return false;
    }
    sessionStorage.setItem(STALE_BUNDLE_RELOAD_KEY, String(Date.now()));
    // eslint-disable-next-line no-console
    console.warn('[stale-bundle] new deploy detected, recovering →', reasonLabel);
    // Hard reload: bypass HTTP cache so we get the fresh index.html.
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.message === 'Script error.' || event.message === 'Script Error.') {
      event.stopImmediatePropagation();
      event.preventDefault();
      return;
    }
    if (_isChunkLoadError(event.message, event.error)) {
      event.preventDefault();
      _attemptStaleBundleRecovery(event.message || 'window.error');
    }
  }, true);
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message || String(event.reason || '');
    if (reason === 'Script error.' || reason === 'Script Error.') {
      event.stopImmediatePropagation();
      event.preventDefault();
      return;
    }
    if (_isChunkLoadError(reason, event.reason)) {
      event.preventDefault();
      _attemptStaleBundleRecovery(reason || 'unhandledrejection');
    }
  }, true);
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
